import { Box, Flex } from "@chakra-ui/react";
import React, { Children, cloneElement, isValidElement } from "react";

interface TabSelectorProps<Value> {
  value: Value;
  onChange: (value: Value) => void;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  children: React.ReactNode;
}

interface TabOptionProps<Value> {
  value: Value;
  disabled?: boolean;
  children: React.ReactNode;
}

export function TabOption<Value>({
  value,
  disabled = false,
  children,
  ...props
}: TabOptionProps<Value> & {
  isActive?: boolean;
  onClick?: () => void;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
}) {
  const { isActive, onClick, size = "md", fullWidth } = props;

  const sizeProps = {
    sm: {
      px: "3",
      py: "1.5",
      fontSize: "14px",
      borderRadius: "md",
    },
    md: {
      px: "4",
      py: "2",
      fontSize: "sm",
      borderRadius: "md",
    },
    lg: {
      px: "5",
      py: "2.5",
      fontSize: "md",
      borderRadius: "lg",
    },
  };

  const currentSize = sizeProps[size];

  return (
    <Box
      px={currentSize.px}
      py={currentSize.py}
      borderRadius={currentSize.borderRadius}
      bg={isActive ? "white" : "transparent"}
      boxShadow={isActive ? "sm" : "none"}
      cursor={disabled ? "not-allowed" : "pointer"}
      opacity={disabled ? 0.5 : 1}
      transition="background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, color 0.2s ease-in-out"
      onClick={() => !disabled && onClick?.()}
      _hover={!disabled && !isActive ? { bg: "gray.50" } : {}}
      flex={fullWidth ? "1" : "none"}
      display="flex"
      alignItems="center"
      justifyContent="center"
      fontWeight={isActive ? "medium" : "normal"}
      color={isActive ? "gray.800" : "gray.600"}
      sx={{ "& *": { pointerEvents: "none" } }}
    >
      {children}
    </Box>
  );
}

export default function TabSelector<Value = string>({
  value,
  onChange,
  size = "md",
  fullWidth = false,
  children,
}: TabSelectorProps<Value>) {
  const sizeProps = {
    sm: {
      borderRadius: "md",
      containerPadding: "1",
    },
    md: {
      borderRadius: "md",
      containerPadding: "1",
    },
    lg: {
      borderRadius: "lg",
      containerPadding: "1.5",
    },
  };

  const currentSize = sizeProps[size];

  return (
    <Flex
      bg="gray.100"
      borderRadius={currentSize.borderRadius}
      p={currentSize.containerPadding}
      display="inline-flex"
      gap="4px"
      width={fullWidth ? "full" : "auto"}
    >
      {Children.map(children, (child) => {
        if (isValidElement<TabOptionProps<Value>>(child)) {
          const isActive = value === child.props.value;
          return cloneElement(child, {
            isActive,
            onClick: () => onChange(child.props.value),
            size,
            fullWidth,
          } as any);
        }
        return child;
      })}
    </Flex>
  );
}
